import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface ConnectOnboardingProps {
  userId: string;
  onAccountCreated?: (accountId: string) => void;
}

export function ConnectOnboarding({ userId, onAccountCreated }: ConnectOnboardingProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast(); 

  const handleOnboarding = async () => {
    try {
      setIsLoading(true);
      
      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: "Please sign in to continue.",
        });
        return;
      }

      // Call Supabase Edge Function to create/get Stripe Connect account
      const { data, error } = await supabase.functions.invoke('stripe-connect-account', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw error;
      }

      if (!data?.accountLink) {
        throw new Error('No account link returned from server');
      }

      // Call onAccountCreated callback if account was created
      if (data.accountId && onAccountCreated) {
        onAccountCreated(data.accountId);
      }

      // Store the current location and session info to return to after Stripe onboarding
      sessionStorage.setItem('stripe_connect_return', window.location.pathname);
      sessionStorage.setItem('stripe_connect_user_id', userId);
      sessionStorage.setItem('stripe_connect_timestamp', Date.now().toString());
      
      // Redirect to Stripe Connect onboarding
      window.location.href = data.accountLink;
    } catch (error) {
      console.error('Onboarding error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to start onboarding process. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 p-6">
      <h2 className="text-2xl font-bold">Connect with Stripe</h2>
      <p className="text-muted-foreground text-center max-w-md">
        To start selling sequences and receive payments, you need to connect your Stripe account.
        You'll be able to track your earnings and request payouts once your balance exceeds $20.
      </p>
      <Button
        onClick={handleOnboarding}
        disabled={isLoading}
        className="w-full max-w-sm"
      >
        {isLoading ? "Setting up..." : "Connect with Stripe"}
      </Button>
    </div>
  );
} 